import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_POLL_ATTEMPTS = 10; // ~20s total (2s interval)

const CheckoutSuccess = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Stripe returns `session_id` on success_url; keep legacy names for safety
  const orderId = params.get('session_id') || params.get('order_id') || params.get('orderId');
  const [state, setState] = useState({ status: 'polling', message: 'Verificăm plata...' });
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);
  const mountedRef = useRef(true);

  const poll = useCallback(async () => {
    if (!mountedRef.current) return;
    if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
      setState({
        status: 'pending',
        message:
          'Nu am primit încă confirmarea plății. Dacă ai plătit cu succes, vei primi email cu detalii în câteva minute.',
      });
      return;
    }
    attemptsRef.current += 1;
    try {
      const { data } = await axios.get(`${API}/checkout/status/${orderId}`);
      if (data.payment_status === 'paid') {
        setState({ status: 'paid', message: 'Plată reușită!' });
        // Clear persisted cart — order is confirmed, start fresh
        localStorage.removeItem('g8_cart');
        sessionStorage.removeItem('g8_checkout_cart');
        setTimeout(() => {
          if (data.order_id) {
            navigate(`/track/${data.order_id}`, { replace: true });
          } else {
            navigate('/', { replace: true });
          }
        }, 1500);
        return;
      }
      if (data.status === 'expired' || data.payment_status === 'unpaid') {
        setState({
          status: 'failed',
          message:
            data.error_message ||
            'Plata a fost anulată sau nu s-a putut procesa. Datele cardului sunt corecte? Poți reîncerca.',
        });
        return;
      }
      // Still pending — Stripe webhook may arrive with a small delay
      timerRef.current = setTimeout(poll, 2000);
    } catch (e) {
      timerRef.current = setTimeout(poll, 2500);
    }
  }, [orderId, navigate]);

  useEffect(() => {
    mountedRef.current = true;
    if (!orderId) {
      setState({ status: 'error', message: 'Sesiune invalidă. Nu am putut identifica comanda.' });
      return () => {};
    }
    poll();
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [orderId, poll]);

  const handleRetryCheck = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    attemptsRef.current = 0;
    setState({ status: 'polling', message: 'Verificăm din nou...' });
    poll();
  };

  const isFailed = state.status === 'failed';
  const isInvalid = state.status === 'error';
  const isPending = state.status === 'pending';

  return (
    <div className="min-h-screen bg-[#E8E4DD] flex items-center justify-center px-4">
      <div
        className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center"
        data-testid="checkout-status-card"
      >
        {state.status === 'polling' && (
          <>
            <Loader2 className="w-16 h-16 text-[#7BA05B] mx-auto animate-spin mb-4" />
            <h1 className="text-2xl font-bold text-[#1F1F1F] mb-2">Verificăm plata</h1>
            <p className="text-[#666]">{state.message}</p>
          </>
        )}
        {state.status === 'paid' && (
          <>
            <CheckCircle2 className="w-16 h-16 text-[#5C9F5F] mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-[#1F1F1F] mb-2">Plată reușită!</h1>
            <p className="text-[#666]">Te redirecționăm către comanda ta...</p>
          </>
        )}
        {isPending && (
          <>
            <RefreshCw className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-[#1F1F1F] mb-2">Plată în verificare</h1>
            <p className="text-[#666] mb-6">{state.message}</p>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleRetryCheck}
                className="bg-[#7BA05B] hover:bg-[#5C8945] text-white font-semibold px-6 py-2 rounded-lg"
                data-testid="retry-check-btn"
              >
                Verifică din nou
              </button>
              <button
                onClick={() => navigate('/')}
                className="bg-transparent border border-[#7BA05B] text-[#7BA05B] hover:bg-[#7BA05B] hover:text-white font-semibold px-6 py-2 rounded-lg transition-colors"
                data-testid="back-home-btn"
              >
                Înapoi la Meniu
              </button>
            </div>
          </>
        )}
        {isFailed && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-[#1F1F1F] mb-2">Plată neconfirmată</h1>
            <p className="text-[#666] mb-6">{state.message}</p>
            <div className="flex flex-col gap-2">
              <button
                onClick={() => navigate('/checkout')}
                className="bg-[#7BA05B] hover:bg-[#5C8945] text-white font-semibold px-6 py-2 rounded-lg"
                data-testid="retry-payment-btn"
              >
                Reîncearcă comanda
              </button>
              <button
                onClick={() => navigate('/')}
                className="bg-transparent border border-[#7BA05B] text-[#7BA05B] hover:bg-[#7BA05B] hover:text-white font-semibold px-6 py-2 rounded-lg transition-colors"
                data-testid="back-home-btn"
              >
                Înapoi la Meniu
              </button>
            </div>
          </>
        )}
        {isInvalid && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-[#1F1F1F] mb-2">Sesiune invalidă</h1>
            <p className="text-[#666] mb-6">{state.message}</p>
            <button
              onClick={() => navigate('/')}
              className="bg-[#7BA05B] hover:bg-[#5C8945] text-white font-semibold px-6 py-2 rounded-lg"
              data-testid="back-home-btn"
            >
              Înapoi la Meniu
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccess;
